import { useCallback, useEffect, useState } from 'react'
import { ButtonsModalSmallCentered, ModalMediumCentered } from '../../../../../../../../../baseComponents/Modal'
import TwinTrans from '../../../../../../../../../baseComponents/TwinTrans'
import useTwinTranslation from '../../../../../../../../../utils/hooks/useTwinTranslation'
import { TwinDictionary } from '../../../../../../../../../utils/globals/dictionary'
import { CustomSelectWithSearchBar } from '../../../../../../../../../forms/CustomSelect'
import { WorkingCenterPayload } from '../../../../../../../../../utils/reducers/company/workingCenters'
import { OnSubmit } from '../../../../../../../../../forms/TwinForm/types'
import useIsLoading from '../../../../../../../../../utils/hooks/useIsLoading'
import { twinFetchPostJSON } from '../../../../../../../../../utils/globals/data'
import { DictRemittanceBank, RemittanceDataTy } from '../types'
import { parseBankRemittanceLots } from '../ModalSelectNewRemittance/functions'
import { ModalEditComponentPropsWithoutSubmit } from '../../../../../../../../../baseComponents/ModalsLayouts/types'

type ModalConfirmNewRemittanceProps = ModalConfirmNewRemittanceLogicProps & ModalEditComponentPropsWithoutSubmit & {}

const ModalConfirmNewRemittance: React.FC<ModalConfirmNewRemittanceProps> = ({ setOpened, completeData, workingCenters, onSubmit }) => {
    const {t} = useTwinTranslation()
    const { dictWCBanks, loading, createRemittance, handleChange } = useModalConfirmNewRemittanceLogic({ completeData, workingCenters, onSubmit })
    const renderThis: JSX.Element[] = []
    if (dictWCBanks){
        for (const key in dictWCBanks) {
            const  bank = dictWCBanks[key]
            renderThis.push(<CustomSelectWithSearchBar key={key} label={t('bankDestinationOf', 'Banco destino de') + ' ' + bank.workingCenterName} items={bank?.items} value={bank?.value} onChange={(val)=>handleChange(parseInt(key), val)} />)
        }
    }
    return (
        <ModalMediumCentered opened={true} setOpened={setOpened}>
            <h2><TwinTrans transKey='createRemittance'>Crear remesa</TwinTrans> - {completeData.name}</h2>
            <span><TwinTrans transKey='onceRemittanceCreatedNotEditable' >Una vez creada la remesa, no podrás editarla</TwinTrans></span>
            <div className='flex flex-col gap-y-20 my-20'>
                {renderThis}
            </div>
            <ButtonsModalSmallCentered loading={loading} confirmText={t('create', 'Crear')} setOpened={() => setOpened(null)} onAccept={createRemittance}></ButtonsModalSmallCentered>
        </ ModalMediumCentered>
    )
}

interface ModalConfirmNewRemittanceLogicProps {
    workingCenters: WorkingCenterPayload
    completeData: RemittanceDataTy
    onSubmit?: OnSubmit
}

const useModalConfirmNewRemittanceLogic = ({ completeData, workingCenters, onSubmit }: ModalConfirmNewRemittanceLogicProps) => {
    const [dictWCBanks, setDictWCBanks] = useState<DictRemittanceBank | null>(null)
    const { loading, startLoading, endLoading } = useIsLoading()

    const handleChange = useCallback((BillingWorkingCenterId: number, BankId: number) => {
        setDictWCBanks((old)=>{
            if(!old){
                return null
            }
            const copyOld= JSON.parse(JSON.stringify(old))
            copyOld[BillingWorkingCenterId].value = BankId
            return copyOld
        })
    }, [setDictWCBanks])

    const getDictWCBanks = useCallback(async () => {
        const dictBillingWC: TwinDictionary = {}
        if (completeData.selectedTableData) {
            for (const element of completeData.selectedTableData) {
                const payMCopyDate = completeData.tableData[completeData?.dictPosTableData[element.id]]
                const BillingWorkingCenterId = payMCopyDate?.['PayMCopy.BillingWorkingCenterId']
                if (!dictBillingWC?.[BillingWorkingCenterId]){
                    dictBillingWC[BillingWorkingCenterId] = { 
                        value: workingCenters?.[BillingWorkingCenterId].bankDefault,
                        items: workingCenters?.[BillingWorkingCenterId].dictBanks || {}, workingCenterName: workingCenters?.[BillingWorkingCenterId].name
                    }
                }
            }
        }
        setDictWCBanks(dictBillingWC)
    }, [completeData, setDictWCBanks, workingCenters])

    const createRemittance = useCallback(async () => {
        startLoading()
        const {dictBillingWorkingCenterBanks, remittanceSelectedIds} = parseBankRemittanceLots(completeData, dictWCBanks)
        const result = await twinFetchPostJSON('/api/app/accounting/collectionAndPayments/createBankRemittance', { name: completeData.name, dictBillingWorkingCenterBanks, remittanceSelectedIds })
        onSubmit?.(result, {})
        endLoading()
    }, [completeData, dictWCBanks, startLoading, endLoading, onSubmit])

    useEffect(() => {
        getDictWCBanks()
    }, [getDictWCBanks])

    return { handleChange, dictWCBanks, loading, createRemittance }
}

export default ModalConfirmNewRemittance